import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { API_URL } from "../config";
import { MathfieldElement } from "mathlive";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import "mathlive";
import "../css/mathlive.core.css";
import UploadExamTheoryPDF from "./GeneralQuestionsTheoryPDF";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import ImageIcon from "@mui/icons-material/Image";
import FunctionsIcon from "@mui/icons-material/Functions";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";

if (!customElements.get("math-field")) {
  customElements.define("math-field", MathfieldElement);
}

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7", dark: "#92680C" },
    error: { main: "#DC2626", light: "#FEE2E2" },
    success: { main: "#059669", light: "#D1FAE5" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 880, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledTextArea = styled("textarea")(({ theme }) => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "12px 14px",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
  lineHeight: 1.65,
  minHeight: 100,
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora', sans-serif",
}));

const ModeToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  background: alpha(theme.palette.primary.main, 0.06),
  borderRadius: 10,
  padding: 3,
  gap: 2,
  width: "fit-content",
}));

const ModeBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  minWidth: 90,
  padding: "6px 16px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "none",
  transition: "all 0.18s ease",
  ...(active
    ? {
        background: "#fff",
        color: theme.palette.primary.main,
        boxShadow: "0 1px 6px rgba(0,0,0,0.10)",
        "&:hover": { background: "#fff" },
      }
    : {
        background: "transparent",
        color: theme.palette.text.disabled,
        "&:hover": {
          background: alpha("#fff", 0.5),
          color: theme.palette.text.secondary,
        },
      }),
}));

const ImageUploadBtn = styled(Button)(({ theme }) => ({
  borderRadius: 9,
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  border: `1px solid ${alpha("#000", 0.12)}`,
  color: "text.secondary",
  background: "#fff",
  px: 2,
  py: 0.9,
  fontFamily: "'Sora', sans-serif",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    background: alpha("#2563EB", 0.04),
  },
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: { bg: alpha("#DC2626", 0.08), color: "#DC2626", hbg: "#DC2626" },
  };
  const s = map[btncolor] || map.edit;
  return {
    minWidth: "unset",
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    fontFamily: "'Sora', sans-serif",
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const QuestionItem = styled(Box, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  padding: "18px 0",
  borderBottom: `1px solid ${alpha("#000", 0.06)}`,
  animation: `${fadeUp} 0.4s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.2s ease",
  "&:last-child": { borderBottom: "none" },
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.02),
    paddingLeft: 8,
    paddingRight: 8,
    borderRadius: 10,
  },
}));

const QIndexBadge = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: 9,
  flexShrink: 0,
  background: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 800,
  fontFamily: "'Sora', sans-serif",
}));

const Spinner = styled(Box)(() => ({
  width: 18,
  height: 18,
  border: "2px solid rgba(37,99,235,0.15)",
  borderTop: "2px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
  display: "inline-block",
  flexShrink: 0,
}));

const SkeletonLine = styled(Box)(({ w = "100%", h = 14, mb = 8 }) => ({
  width: w,
  height: h,
  borderRadius: 6,
  background: alpha("#000", 0.07),
  marginBottom: mb,
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SearchBox = styled(Box)(() => ({
  position: "relative",
  "& input": { paddingLeft: 40 },
  "& .search-icon": {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 16,
    color: "#9CA3AF",
    pointerEvents: "none",
  },
}));

const StyledSearchInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminTheoryQuestionPage() {
  const { theoryYearId } = useParams();
  const navigate = useNavigate();
  const questionRef = useRef(null);
  const answerRef = useRef(null);
  const qImageInputRef = useRef(null);
  const aImageInputRef = useRef(null);
  const [mode, setMode] = useState("text");
  const [theoryQuestions, setTheoryQuestions] = useState([]);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [qImageUri, setQImageUri] = useState(null);
  const [sImageUri, setSImageUri] = useState(null);
  const [qimagePreview, setQImagePreview] = useState(null);
  const [simagePreview, setSImagePreview] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchTheoryQuestions = useCallback(() => {
    setFetching(true);
    fetch(`${API_URL}/admin/theoryQuestions/${theoryYearId}/`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // support both { questions: [...] } and plain array
        const questions = Array.isArray(data) ? data : data.questions || [];
        console.log(questions);
        setTheoryQuestions(questions);
        setFetching(false);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        alert("Failed to fetch theory questions. Check your API endpoint.");
        setFetching(false);
      });
  }, [theoryYearId]);

  useEffect(() => {
    fetchTheoryQuestions();
  }, [fetchTheoryQuestions]);

  // ── Form helpers ───────────────────────────────────────────────────────────
  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setQImageUri(null);
    setSImageUri(null);
    setQImagePreview(null);
    setSImagePreview(null);
    setEditingQuestionId(null);
    if (questionRef.current) questionRef.current.value = "";
    if (answerRef.current) answerRef.current.value = "";
  };

  const submitTheoryQuestion = () => {
    if (!question || !answer) {
      alert("Both question and answer are required.");
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append("question", question);
    formData.append("answer", answer);
    if (qImageUri) formData.append("quesImg", qImageUri);
    if (sImageUri) formData.append("ansImg", sImageUri);

    const url = editingQuestionId
      ? `${API_URL}/admin/theoryQuestion/${editingQuestionId}/`
      : `${API_URL}/admin/theoryQuestions/${theoryYearId}/`;
    const method = editingQuestionId ? "PUT" : "POST";

    fetch(url, { method, body: formData })
      .then((res) => res.json())
      .then((data) => {
        if (editingQuestionId) {
          setTheoryQuestions((prev) =>
            prev.map((i) => (i.id === editingQuestionId ? data : i))
          );
        } else {
          setTheoryQuestions((prev) => [data, ...prev]);
        }
        resetForm();
      })
      .catch(() =>
        alert(
          `Failed to ${editingQuestionId ? "update" : "add"} theory question.`
        )
      )
      .finally(() => setIsLoading(false));
  };

  const editTheoryQuestion = (item) => {
    setEditingQuestionId(item.id);
    setQuestion(item.question);
    setAnswer(item.answer);
    setQImagePreview(item.quesImg || null);
    setSImagePreview(item.ansImg || null);
    if (questionRef.current) questionRef.current.value = item.question;
    if (answerRef.current) answerRef.current.value = item.answer;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteTheoryQuestion = (id) => {
    if (!window.confirm("Delete this theory question?")) return;
    fetch(`${API_URL}/admin/theoryQuestion/${id}/`, { method: "DELETE" })
      .then(() => {
        setTheoryQuestions((prev) => prev.filter((i) => i.id !== id));
        if (editingQuestionId === id) resetForm();
      })
      .catch(() => alert("Failed to delete theory question."));
  };

  // ── Image upload ───────────────────────────────────────────────────────────
  const removeBackgroundWithRemoveBg = async (file) => {
    const fd = new FormData();
    fd.append("image_file", file);
    fd.append("size", "auto");
    const res = await fetch("https://api.remove.bg/v1.0/removebg", {
      method: "POST",
      headers: { "X-Api-Key": "hxRA6YNfZzrnsXgMYv9ZBYCh" },
      body: fd,
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.blob();
  };

  const handleQuestionImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsRemovingBg(true);
    try {
      const blob = await removeBackgroundWithRemoveBg(file);
      setQImageUri(blob);
      setQImagePreview(URL.createObjectURL(blob));
    } catch {
      setQImageUri(file);
      setQImagePreview(URL.createObjectURL(file));
    } finally {
      setIsRemovingBg(false);
    }
  };

  const handleAnswerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsRemovingBg(true);
    try {
      const blob = await removeBackgroundWithRemoveBg(file);
      setSImageUri(blob);
      setSImagePreview(URL.createObjectURL(blob));
    } catch {
      setSImageUri(file);
      setSImagePreview(URL.createObjectURL(file));
    } finally {
      setIsRemovingBg(false);
    }
  };

  // ── Render latex ───────────────────────────────────────────────────────────
  const renderContent = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return <InlineMath key={index} math={part.slice(2, -2)} />;
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return <InlineMath key={index} math={part.slice(1, -1)} />;
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };
  // ── Filtered list ──────────────────────────────────────────────────────────
  const filteredQuestions = theoryQuestions.filter(
    (q) =>
      !searchQuery ||
      q.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.answer?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editingQuestionId
                  ? "Edit Theory Question"
                  : "Theory Questions"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Year ID:{" "}
                <strong style={{ color: "#4B5563" }}>{theoryYearId}</strong>
              </Typography>
            </Box>
            <Chip
              label={editingQuestionId ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editingQuestionId
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editingQuestionId ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editingQuestionId
                    ? alpha("#C4922A", 0.25)
                    : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── PDF Upload Card ── */}
          <SectionCard delay={40}>
            <CardLabel>Import from PDF</CardLabel>
            <UploadExamTheoryPDF yearId={theoryYearId} />
          </SectionCard>

          {/* ── Form Card ── */}
          <SectionCard delay={80}>
            <CardLabel>
              {editingQuestionId ? "Edit Question" : "Add New Question"}
            </CardLabel>

            {/* Mode toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <FieldLabel sx={{ mb: 0 }}>Input Mode</FieldLabel>
              <ModeToggle>
                <ModeBtn
                  active={mode === "text" ? 1 : 0}
                  startIcon={
                    <TextFieldsIcon sx={{ fontSize: "14px !important" }} />
                  }
                  onClick={() => setMode("text")}
                >
                  Text
                </ModeBtn>
                <ModeBtn
                  active={mode === "math" ? 1 : 0}
                  startIcon={
                    <FunctionsIcon sx={{ fontSize: "14px !important" }} />
                  }
                  onClick={() => setMode("math")}
                >
                  Math
                </ModeBtn>
              </ModeToggle>
            </Box>

            {/* ── Question field ── */}
            <Box sx={{ mb: 2.5 }}>
              <FieldLabel>Question *</FieldLabel>

              {mode === "text" ? (
                <StyledTextArea
                  placeholder="Enter the theory question here…"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${alpha("#000", 0.1)}`,
                    borderRadius: "10px",
                    background: alpha("#F0F2F8", 0.8),
                    overflow: "hidden",
                    "&:focus-within": {
                      border: "1.5px solid #2563EB",
                      background: "#fff",
                      boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
                    },
                  }}
                >
                  <math-field
                    ref={questionRef}
                    onInput={(e) => setQuestion(e.target.getValue())}
                    style={{
                      width: "100%",
                      minHeight: 100,
                      border: "none",
                      padding: 12,
                      fontSize: 16,
                      background: "transparent",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Question image */}
            <Box sx={{ mb: 3 }}>
              <FieldLabel>Question Image (optional)</FieldLabel>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                <ImageUploadBtn
                  component="label"
                  startIcon={<ImageIcon sx={{ fontSize: "15px !important" }} />}
                >
                  {qimagePreview ? "Change Image" : "Upload Question Image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={qImageInputRef}
                    onChange={handleQuestionImageUpload}
                  />
                </ImageUploadBtn>
                {isRemovingBg && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Spinner />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "primary.main",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Removing background…
                    </Typography>
                  </Stack>
                )}
              </Stack>
              {qimagePreview && (
                <Box
                  component="img"
                  src={qimagePreview}
                  alt="Question"
                  sx={{
                    mt: 1.5,
                    maxHeight: 160,
                    maxWidth: "100%",
                    borderRadius: 2,
                    display: "block",
                    border: `1px solid ${alpha("#000", 0.08)}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                  }}
                />
              )}
            </Box>

            {/* ── Answer field ── */}
            <Box sx={{ mb: 2.5 }}>
              <FieldLabel>Answer *</FieldLabel>
              {mode === "text" ? (
                <StyledTextArea
                  placeholder="Enter the model answer here…"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  style={{ minHeight: 120 }}
                />
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${alpha("#000", 0.1)}`,
                    borderRadius: "10px",
                    background: alpha("#F0F2F8", 0.8),
                    overflow: "hidden",
                    "&:focus-within": {
                      border: "1.5px solid #2563EB",
                      background: "#fff",
                      boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
                    },
                  }}
                >
                  <math-field
                    ref={answerRef}
                    onInput={(e) => setAnswer(e.target.getValue())}
                    style={{
                      width: "100%",
                      minHeight: 100,
                      border: "none",
                      padding: 12,
                      fontSize: 16,
                      background: "transparent",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Answer image */}
            <Box sx={{ mb: 3 }}>
              <FieldLabel>Answer Image (optional)</FieldLabel>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                <ImageUploadBtn
                  component="label"
                  startIcon={<ImageIcon sx={{ fontSize: "15px !important" }} />}
                >
                  {simagePreview ? "Change Image" : "Upload Answer Image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={aImageInputRef}
                    onChange={handleAnswerImageUpload}
                  />
                </ImageUploadBtn>
                {isRemovingBg && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Spinner />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "primary.main",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Removing background…
                    </Typography>
                  </Stack>
                )}
              </Stack>
              {simagePreview && (
                <Box
                  component="img"
                  src={simagePreview}
                  alt="Answer"
                  sx={{
                    mt: 1.5,
                    maxHeight: 160,
                    maxWidth: "100%",
                    borderRadius: 2,
                    display: "block",
                    border: `1px solid ${alpha("#000", 0.08)}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                  }}
                />
              )}
            </Box>

            {/* Submit */}
            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={
                  editingQuestionId ? (
                    <CheckIcon sx={{ fontSize: "16px !important" }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                disabled={isLoading || isRemovingBg}
                onClick={submitTheoryQuestion}
              >
                {isLoading
                  ? "Saving…"
                  : editingQuestionId
                  ? "Update Question"
                  : "Add Question"}
              </PrimaryBtn>
              {editingQuestionId && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Questions List Card ── */}
          <SectionCard delay={140}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>Questions</CardLabel>
              {!fetching && (
                <Chip
                  label={`${filteredQuestions.length} of ${
                    theoryQuestions.length
                  } question${theoryQuestions.length !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    background: alpha("#2563EB", 0.08),
                    color: "#2563EB",
                    border: `1px solid ${alpha("#2563EB", 0.18)}`,
                  }}
                />
              )}
            </Box>

            {/* Search */}
            <SearchBox sx={{ mb: 3 }}>
              <SearchIcon className="search-icon" />
              <StyledSearchInput
                placeholder="Search questions or answers…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBox>

            {/* ── Skeleton ── */}
            {fetching ? (
              <Box>
                {[...Array(4)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      py: 2,
                      borderBottom: `1px solid ${alpha("#000", 0.06)}`,
                      animationDelay: `${i * 100}ms`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "9px",
                          background: alpha("#000", 0.07),
                          flexShrink: 0,
                          animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
                          animationDelay: `${i * 120}ms`,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <SkeletonLine w="80%" h={14} mb={8} />
                        <SkeletonLine w="55%" h={12} mb={16} />
                        <Stack direction="row" spacing={1}>
                          <SkeletonLine w={70} h={28} mb={0} />
                          <SkeletonLine w={70} h={28} mb={0} />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : filteredQuestions.length === 0 ? (
              /* ── Empty ── */
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <QuestionAnswerIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  {searchQuery
                    ? `No questions match "${searchQuery}"`
                    : "No questions added yet"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  {searchQuery
                    ? "Try a different search term"
                    : "Use the form above to add your first question"}
                </Typography>
              </Box>
            ) : (
              /* ── Question list ── */
              filteredQuestions.map((item, i) => (
                <QuestionItem key={item.id} itemdelay={i * 50}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <QIndexBadge>{item.number}</QIndexBadge>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Question */}
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            color: "text.disabled",
                            fontFamily: "'Sora', sans-serif",
                            mb: 0.4,
                          }}
                        >
                          Question
                        </Typography>
                        <Typography
                          component="div"
                          sx={{
                            fontSize: 14,
                            color: "text.primary",
                            fontFamily: "'Sora', sans-serif",
                            lineHeight: 1.65,
                            wordBreak: "break-word",
                          }}
                        >
                          {renderContent(item.question)}
                        </Typography>
                        {item.quesImg && (
                          <Box
                            component="img"
                            src={item.quesImg}
                            alt="Q"
                            sx={{
                              mt: 1,
                              maxHeight: 100,
                              borderRadius: 1.5,
                              border: `1px solid ${alpha("#000", 0.07)}`,
                            }}
                          />
                        )}
                      </Box>

                      {/* Answer */}
                      <Box
                        sx={{
                          background: alpha("#059669", 0.04),
                          border: `1px solid ${alpha("#059669", 0.15)}`,
                          borderRadius: 2,
                          px: 1.5,
                          py: 1,
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            color: "#059669",
                            fontFamily: "'Sora', sans-serif",
                            mb: 0.4,
                          }}
                        >
                          Answer
                        </Typography>
                        <Typography
                          component="div"
                          sx={{
                            fontSize: 13,
                            color: "text.secondary",
                            fontFamily: "'Sora', sans-serif",
                            lineHeight: 1.6,
                            wordBreak: "break-word",
                          }}
                        >
                          {renderContent(item.answer)}
                        </Typography>
                        {item.ansImg && (
                          <Box
                            component="img"
                            src={item.ansImg}
                            alt="A"
                            sx={{
                              mt: 1,
                              maxHeight: 100,
                              borderRadius: 1.5,
                              border: `1px solid ${alpha("#000", 0.07)}`,
                            }}
                          />
                        )}
                      </Box>

                      {/* Actions */}
                      <Stack direction="row" spacing={0.8}>
                        <Tooltip title="Edit question" arrow>
                          <ActionBtn
                            btncolor="edit"
                            onClick={() => editTheoryQuestion(item)}
                          >
                            <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                            Edit
                          </ActionBtn>
                        </Tooltip>
                        <Tooltip title="Delete question" arrow>
                          <ActionBtn
                            btncolor="delete"
                            onClick={() => deleteTheoryQuestion(item.id)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
                            Delete
                          </ActionBtn>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Stack>
                </QuestionItem>
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
